import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Sliders, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '../utils/apiConfig';

/**
 * OrbDetourSlider — Phase B+ live UX
 * ----------------------------------
 * Sits inside the Orb panel under the Match-Profile.
 * Moving the slider triggers a debounced (350ms) GET /rides/search/corridor
 * with the current `max_detour_km`. AbortController kills any in-flight
 * request so only the latest slider value resolves.
 *
 * Server still enforces detour caps at booking time via
 * booking_corridor.validate_pickup_dropoff_against_route() — this slider is
 * a UX filter, not a security boundary.
 *
 * Props:
 *   coords     — { from: {lat,lng}, to: {lat,lng} } (required to query)
 *   onResults(rides[]) — callback every time a search completes
 *   onError(err)        — optional, otherwise silent
 *   initialKm  — number, default 0 (= "any")
 *   visible    — boolean — render the panel only when needed
 */
export const OrbDetourSlider = ({
  coords,
  onResults,
  onError,
  initialKm = 0,
  visible = true,
}) => {
  const { t } = useTranslation();
  const [maxDetourKm, setMaxDetourKm] = useState(initialKm);
  const [loading, setLoading] = useState(false);
  const [resultCount, setResultCount] = useState(null);

  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const runSearch = useCallback(
    async (km) => {
      if (!coords?.from?.lat || !coords?.to?.lat) return;
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      try {
        const params = {
          from_lat: coords.from.lat,
          from_lng: coords.from.lng,
          to_lat: coords.to.lat,
          to_lng: coords.to.lng,
          pickup_radius_km: 5,
          dropoff_radius_km: 5,
        };
        if (km > 0) params.max_detour_km = km;
        const res = await axios.get(`${API}/rides/search/corridor`, {
          params,
          signal: controller.signal,
        });
        if (!mountedRef.current) return;
        const rides = res.data?.rides || [];
        setResultCount(rides.length);
        if (onResults) onResults(rides, res.data);
      } catch (e) {
        if (e.name === 'CanceledError' || e.name === 'AbortError') return;
        if (mountedRef.current && onError) onError(e);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [coords, onResults, onError]
  );

  // Debounce slider changes
  useEffect(() => {
    if (!visible) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(maxDetourKm), 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [maxDetourKm, visible, runSearch]);

  if (!visible) return null;

  const pillColor = maxDetourKm === 0 ? '#94a3b8' : '#22c55e';

  return (
    <div
      data-testid="orb-detour-slider"
      style={{
        marginTop: 14,
        padding: '12px 16px',
        width: 'min(420px, 90vw)',
        background: 'rgba(15,23,42,0.65)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 14,
        backdropFilter: 'blur(10px)',
        color: '#fff',
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 12, color: 'rgba(255,255,255,0.85)', fontWeight: 600,
          letterSpacing: 0.2,
        }}>
          <Sliders size={13} />
          {t('orb.detourLabel', 'Maximaler Umweg')}
        </span>
        <span
          data-testid="orb-detour-value"
          style={{
            fontSize: 12, fontWeight: 700,
            color: pillColor,
            background: `${pillColor}26`,
            padding: '2px 10px', borderRadius: 999,
          }}
        >
          {maxDetourKm === 0 ? t('orb.detourAny', 'beliebig') : `≤ ${maxDetourKm} km`}
        </span>
      </div>
      <input
        type="range"
        data-testid="orb-detour-range"
        min="0"
        max="20"
        step="1"
        value={maxDetourKm}
        onChange={(e) => setMaxDetourKm(parseInt(e.target.value, 10))}
        aria-label={t('orb.detourLabel', 'Maximaler Umweg')}
        style={{ width: '100%', accentColor: '#22c55e' }}
      />
      <div
        data-testid="orb-detour-status"
        style={{
          marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: 11, color: 'rgba(255,255,255,0.55)',
        }}
      >
        <span>{t('orb.detourHint', 'Live-Filter — bewege den Slider')}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {loading ? (
            <>
              <Loader2 size={11} className="spinning" />
              {t('orb.searching', 'Suche…')}
            </>
          ) : resultCount !== null ? (
            <span style={{ color: resultCount > 0 ? '#22c55e' : '#fbbf24' }}>
              {t('orb.detourHits', '{{n}} Treffer', { n: resultCount })}
            </span>
          ) : null}
        </span>
      </div>
    </div>
  );
};

export default OrbDetourSlider;
